if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      variantPrefetches = new Map();
      variantRequestId = 0;
      variantPrefetchHandler = (event) => this.prefetchVariant(event);
      variantPrefetchObserver = undefined;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initializeVariantPrefetch();
        this.addEventListener('pointerover', this.variantPrefetchHandler);
        this.addEventListener('focusin', this.variantPrefetchHandler);
        this.addEventListener('touchstart', this.variantPrefetchHandler, { passive: true });

        this.initQuantityHandlers();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.variantPrefetchObserver?.disconnect();
        this.removeEventListener('pointerover', this.variantPrefetchHandler);
        this.removeEventListener('focusin', this.variantPrefetchHandler);
        this.removeEventListener('touchstart', this.variantPrefetchHandler);
        this.variantPrefetches.forEach((entry) => entry.controller.abort());
        this.variantPrefetches.clear();
        this.abortController?.abort();
        this.variantRequestId++;
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        this.productForms.forEach((productForm) => {
          productForm.toggleSubmitButton?.(true);
          productForm.handleErrorMessage?.();
        });
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      initializeVariantPrefetch() {
        if (this.dataset.updateUrl !== 'true' || this.dataset.originalSection) return;
        if (typeof IntersectionObserver === 'undefined' || this.getSelectedVariant(this)?.available !== false) return;
        const picker = this.variantSelectors;
        const selected = picker?.querySelectorAll('select option[selected], fieldset input:checked');
        if (selected?.length !== 1 || selected[0].tagName !== 'INPUT') return;
        const radios = Array.from(picker.querySelectorAll('input[type="radio"]'));
        const next = radios.slice(radios.indexOf(selected[0]) + 1).find((input) =>
          input.name === selected[0].name && !input.disabled && !input.classList.contains('disabled')
        );
        if (!next) return;
        this.variantPrefetchObserver = new IntersectionObserver((entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          this.variantPrefetchObserver.disconnect();
          const prefetch = () => {
            if (this.isConnected && next.isConnected) this.prefetchVariant({ target: next });
          };
          if (typeof window.requestIdleCallback === 'function') window.requestIdleCallback(prefetch, { timeout: 1000 });
          else setTimeout(prefetch, 0);
        });
        this.variantPrefetchObserver.observe(picker);
      }

      prefetchVariant(event) {
        if (this.dataset.updateUrl !== 'true' || this.dataset.originalSection) return;
        const connection = navigator.connection;
        if (connection?.saveData || ['slow-2g', '2g'].includes(connection?.effectiveType)) return;

        const picker = this.variantSelectors;
        if (!picker || !picker.contains(event.target)) return;
        const label = event.target.closest('label');
        const input = label
          ? Array.from(picker.querySelectorAll('input[type="radio"]')).find((radio) => radio.id === label.htmlFor)
          : event.target.closest('input[type="radio"]');
        if (!input || input.checked || input.disabled || !input.dataset.optionValueId) return;
        if (input.dataset.productUrl && input.dataset.productUrl !== this.dataset.url) return;

        const selected = Array.from(picker.querySelectorAll('select option[selected], fieldset input:checked'));
        if (!selected.some((option) => option.name === input.name)) return;
        const optionValues = selected.map((option) =>
          option.name === input.name ? input.dataset.optionValueId : option.dataset.optionValueId
        );
        const requestUrl = this.buildRequestUrlWithParams(this.dataset.url, optionValues);
        const now = Date.now();
        this.variantPrefetches.forEach((entry, url) => {
          if ((entry.expiresAt || entry.startedAt + 5000) <= now) this.variantPrefetches.delete(url);
        });
        if (this.variantPrefetches.has(requestUrl) || this.variantPrefetches.size >= 2) return;

        const entry = { controller: new AbortController(), startedAt: now, expiresAt: 0 };
        this.variantPrefetches.set(requestUrl, entry);
        entry.promise = this.fetchProductInfoText(requestUrl, entry.controller.signal, 'low')
          .then((text) => {
            entry.expiresAt = Date.now() + 5000;
            return text;
          })
          .catch(() => {
            if (this.variantPrefetches.get(requestUrl) === entry) this.variantPrefetches.delete(requestUrl);
            return null;
          });
      }

      fetchProductInfoText(requestUrl, signal, priority = 'auto') {
        return fetch(requestUrl, { signal, priority }).then((response) => {
          if (!response.ok) throw new Error(`Product section request failed: ${response.status}`);
          return response.text();
        });
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        const requestId = ++this.variantRequestId;
        const entry = this.variantPrefetches.get(requestUrl);
        this.variantPrefetches.delete(requestUrl);
        const fresh = entry && !entry.controller.signal.aborted &&
          (entry.expiresAt || entry.startedAt + 5000) > Date.now();
        this.abortController = fresh ? entry.controller : new AbortController();
        const controller = this.abortController;
        const response = fresh
          ? entry.promise.then((text) => text ?? this.fetchProductInfoText(requestUrl, controller.signal))
          : this.fetchProductInfoText(requestUrl, controller.signal);

        return response
          .then((responseText) => {
            if (controller.signal.aborted || requestId !== this.variantRequestId) return;
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            if (controller.signal.aborted || requestId !== this.variantRequestId) return;
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          const updateAllSourcesFromDestination = (selector, shouldHide = (source) => false) => {
            const sources = Array.from(html.querySelectorAll(selector));
            const destinations = Array.from(this.querySelectorAll(selector));
            destinations.forEach((destination, index) => {
              const source = sources[index];
              if (!source) return;
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            });
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('price-desc');
          updateSourceFromDestination('StickyPrice');
          updateSourceFromDestination('StickyappPrice');
          updateSourceFromDestination('Title');
          updateSourceFromDestination('Titlesize');
          updateSourceFromDestination('SaleBadges');
          updateAllSourcesFromDestination('[data-variant-title]');
          this.updateModelSelectorVisibility(html);
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          const disableSubmit =
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true;
          this.productForms.forEach((productForm) => {
            productForm.toggleSubmitButton?.(disableSubmit, window.variantStrings.soldOut);
          });

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateModelSelectorVisibility(html) {
        this.querySelectorAll('[data-tcl-model-selector]').forEach((destination) => {
          const source = html.getElementById(
            `TCLModelSelector-${this.sectionId}-${destination.dataset.tclModelSelector}`
          );
          if (!source) return;
          destination.classList.toggle('hidden', source.classList.contains('hidden'));
          destination.dataset.tclEol = source.dataset.tclEol;
        });
      }

      updateVariantInputs(variantId) {
        const productForms = new Set([
          ...Array.from(this.productForms, (productForm) => productForm.querySelector('form')),
          ...this.querySelectorAll(
            `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
          ),
        ]);

        productForms.forEach((productForm) => {
          const input = productForm?.querySelector('input[name="id"]');
          if (!input) return;
          input.value = variantId ?? '';
          input.toggleAttribute('disabled', !variantId);
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForms.forEach((productForm) => {
          productForm.toggleSubmitButton?.(true, window.variantStrings.unavailable);
        });

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
            if (selector === '.quantity__label') {
              const updatedAriaLabelledBy = updated.getAttribute('aria-labelledby');
              if (updatedAriaLabelledBy) {
                current.setAttribute('aria-labelledby', updatedAriaLabelledBy);
                // Update the referenced visually hidden element
                const labelId = updatedAriaLabelledBy;
                const currentHiddenLabel = document.getElementById(labelId);
                const updatedHiddenLabel = html.getElementById(labelId);
                if (currentHiddenLabel && updatedHiddenLabel) {
                  currentHiddenLabel.textContent = updatedHiddenLabel.textContent;
                }
              }
            }
          }
        }
      }

      get productForm() {
        return this.productForms.values().next().value;
      }

      get productForms() {
        // The modal is moved outside product-info; match its section and product,
        // while excluding recommendation cards that share only the section id.
        const forms = [
          ...this.querySelectorAll('product-form'),
          ...document.querySelectorAll(`product-form[data-section-id="${this.dataset.section}"]`),
        ];
        return new Set(
          forms.filter((productForm) => {
            if (productForm.dataset.tclProductId) {
              return productForm.dataset.sectionId === this.dataset.section &&
                productForm.dataset.tclProductId === this.dataset.productId;
            }
            // Preserve compatibility with older markup during section refreshes.
            return !!productForm.querySelector(`form[id="product-form-${this.dataset.section}"]`);
          })
        );
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}
